import logoImg from 'assets/images/logo.svg';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Image from 'quarks/Image';
import Link from 'quarks/Link';
import Text from 'quarks/Text';
import type { BasicProps } from 'quarks/interpolations/basic';

import Modal from 'molecules/Modal/Modal';

import ComponentButton from 'components/Button/ComponentButton';

import { numberWithCommas } from 'utils/functions';

import type { FC } from 'react';

interface PopupProps extends BasicProps {
  open: boolean;
  onClose: () => void;
  values: number[];
}

const Popup: FC<PopupProps> = ({ open, onClose, values, ...props }) => (
  <Modal open={open} onClose={onClose} width="90%" maxWidth="770px" marginX="auto">
    <Container position="relative" backgroundColor="common-white" {...props}>
      <Flex width="100%" justifyContent="space-between" flexDirection="column" md={{ flexDirection: 'row' }}>
        <Flex
          flexDirection="column"
          width="100%"
          paddingAll={24}
          gap="16px"
          md={{ width: '40%', paddingAll: 48, gap: '32px' }}
        >
          <Text fontSize="h5" lineHeight="h5" textColor="gray-700" fontWeight="bold">
            Cost Savings
          </Text>
          <Flex
            width="100%"
            justifyContent="space-between"
            flexWrap="wrap"
            gap="16px"
            md={{ gap: '32px', flexDirection: 'column' }}
          >
            <Flex
              flexDirection="row"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
              sm={{ flexDirection: 'column', width: 'fit-content', alignItems: 'flex-start' }}
            >
              <Text
                textStyle="textLg"
                textColor="green-300"
                fontWeight="semiBold"
                md={{ textStyle: 'textLg', fontWeight: 'semiBold' }}
              >
                Collections
              </Text>
              <Flex alignItems="flex-end" gap="4px">
                <Text
                  fontSize="h6"
                  lineHeight="h6"
                  textColor="gray-700"
                  fontWeight="bold"
                  fontStyle="italic"
                  md={{ fontSize: 'h5', lineHeight: 'h5' }}
                >
                  ${numberWithCommas(Math.round(values[0]))}
                </Text>
                <Text textColor="gray-700">/month*</Text>
              </Flex>
            </Flex>
            <Flex
              flexDirection="row"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
              sm={{ flexDirection: 'column', width: 'fit-content', alignItems: 'flex-start' }}
            >
              <Text
                textStyle="textLg"
                textColor="navy-900"
                fontWeight="semiBold"
                md={{ textStyle: 'textLg', fontWeight: 'semiBold' }}
              >
                Routing
              </Text>
              <Flex alignItems="flex-end" gap="4px">
                <Text
                  fontSize="h6"
                  lineHeight="h6"
                  textColor="gray-700"
                  fontWeight="bold"
                  fontStyle="italic"
                  md={{ fontSize: 'h5', lineHeight: 'h5' }}
                >
                  ${numberWithCommas(Math.round(values[1]))}
                </Text>
                <Text textColor="gray-700">/month*</Text>
              </Flex>
            </Flex>
            <Flex
              flexDirection="row"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
              sm={{ flexDirection: 'column', width: 'fit-content', alignItems: 'flex-start' }}
            >
              <Text
                textStyle="textLg"
                textColor="blue-600"
                fontWeight="semiBold"
                md={{ textStyle: 'textLg', fontWeight: 'semiBold' }}
              >
                Paperless
              </Text>
              <Flex alignItems="flex-end" gap="4px">
                <Text
                  fontSize="h6"
                  lineHeight="h6"
                  textColor="gray-700"
                  fontWeight="bold"
                  fontStyle="italic"
                  md={{ fontSize: 'h5', lineHeight: 'h5' }}
                >
                  ${numberWithCommas(Math.round(values[2]))}
                </Text>
                <Text textColor="gray-700">/month*</Text>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
          height="100%"
          paddingAll={24}
          gap="8px"
          textAlign="center"
          md={{ width: '60%', paddingAll: 48 }}
          css={`
            background: linear-gradient(0deg, #00aecf, #1a1353);
          `}
        >
          <Text
            fontSize="h6"
            lineHeight="h6"
            textColor="common-white"
            fontWeight="bold"
            sm={{ fontSize: 'h5', lineHeight: 'h5' }}
          >
            You Could be Saving
          </Text>
          <Text
            fontSize="h4"
            lineHeight="h4"
            textColor="common-white"
            fontWeight="bold"
            sm={{ fontSize: 'h3', lineHeight: 'h3' }}
          >
            ${numberWithCommas(Math.round(values[0] + values[1] + values[2]))}
          </Text>
          <Text
            fontSize="textMd"
            lineHeight="textMd"
            textColor="common-white"
            fontWeight="bold"
            sm={{ fontSize: 'textLg', lineHeight: 'textLg' }}
          >
            per month* with
          </Text>
          <Link href="/">
            <Image image={{ file: { url: logoImg } }} alt="Logo" height="100%" width="100%" />
          </Link>
          <Text
            fontSize="textSm"
            lineHeight="textSm"
            textColor="common-white"
            marginBottom={8}
            sm={{ fontSize: 'textMd', lineHeight: 'textMd' }}
          >
            Schedule a free demo to see our software in action!
          </Text>
          <ComponentButton label="Get a free demo" link="/free-demo" />
          <Text fontSize="textXs" lineHeight="textXs" textColor="common-white" marginTop={8}>
            *The cost savings are only estimates and are not a guarantee of savings.
          </Text>
        </Flex>
      </Flex>
    </Container>
  </Modal>
);

export default Popup;
